<template>
  <v-dialog
    v-if="currentMutabakat"
    :value="true"
    no-click-animation
    scrollable
    width="820"
    :retain-focus="false"
    @input="(val) => !val && dismiss()"
  >
    <v-card>
      <v-card-title class="grey">
        <div class="d-flex align-center" style="width: 100%">
          <v-icon color="white" size="22">
            {{ icons.mdiFileDocumentOutline }}
          </v-icon>
          <div class="text-subtitle-1 font-weight-bold ms-3 white--text">
            {{ currentMutabakat.mutabakat_no }} Mutabakat
          </div>
          <v-spacer />
          <v-btn color="grey" depressed fab small @click="dismiss">
            <v-icon>{{ icons.mdiCloseCircleOutline }}</v-icon>
          </v-btn>
        </div>
      </v-card-title>
      <v-divider />

      <v-alert class="ma-0 py-2" tile dense :type="currentMutabakat.must_approve ? 'error' : 'warning'" text>
        <div class="font-weight-bold">
          <template v-if="currentMutabakat.must_approve">
            Mutabakat gönderiminin üzerinden {{ currentMutabakat.passed_days }} gün geçti, onaylama süresi geçmiştir.
          </template>
          <template v-else>
            Onayınızı bekleyen mutabakatınız bulunuyor ({{ currentMutabakat.passed_days }} gün önce gönderildi).
            <template v-if="currentMutabakat.remaining_days != null">
              Onaylamak için son {{ currentMutabakat.remaining_days }} gününüz kaldı.
            </template>
          </template>
        </div>
        <div class="text-caption">
          <template v-if="currentMutabakat.must_approve">
            Devam edebilmek için lütfen mutabakatı onaylayın veya reddedin.
          </template>
          <template v-else>
            Sipariş verebilmek için mutabakatı onaylamanız veya reddetmeniz gerekiyor.
          </template>
        </div>
      </v-alert>

      <v-card-text class="pa-0">
        <div v-if="pdfLoading" class="d-flex justify-center align-center" style="height: 480px">
          <v-progress-circular indeterminate color="primary" />
        </div>
        <div v-else-if="pdfError" class="pa-6 text-center">
          {{ pdfError }}
        </div>
        <div v-else-if="pdfUrl" class="d-flex justify-center" style="max-height: 70svh; overflow: auto">
          <img
            v-if="isImageUrl(pdfUrl)"
            :src="pdfUrl"
            style="max-width: 100%; display: block"
          />
          <embed
            v-else
            :src="`${pdfUrl}#toolbar=0&navpanes=0&statusbar=0&view=FitH`"
            type="application/pdf"
            style="width: 100%; height: 70svh"
          />
        </div>
      </v-card-text>

      <v-divider />

      <!-- Onay/Ret Formu -->
      <v-expand-transition>
        <div v-if="responseAction">
          <v-card-text class="pb-0">
            <div class="text-subtitle-2 font-weight-bold mb-3">
              {{ responseAction === "RET" ? "Mutabakatı Reddet" : "Mutabakatı Onayla" }}
            </div>
            <v-textarea
              v-model="note"
              outlined
              dense
              rows="3"
              counter="255"
              maxlength="255"
              hide-details="auto"
              :label="responseAction === 'RET' ? 'Ret nedeninizi kısaca açıklayınız' : 'Açıklama (opsiyonel)'"
              :rules="responseAction === 'RET' ? [(v) => !!(v && v.trim()) || 'Ret nedeninizi yazınız']  : []"
            />
            <v-file-input
              v-model="file"
              outlined
              dense
              class="mt-3"
              hide-details="auto"
              label="Dosya Ekle (opsiyonel)"
              accept="image/*,application/pdf"
              :prepend-icon="null"
              :prepend-inner-icon="icons.mdiPaperclip"
              hint="Yalnızca görsel veya PDF dosyası ekleyebilirsiniz"
              persistent-hint
              show-size
            />
          </v-card-text>
        </div>
      </v-expand-transition>

      <v-card-actions class="pa-4">
        <template v-if="responseAction">
          <v-btn text class="text-capitalize" :disabled="approvalLoading" @click="cancelResponse">
            Vazgeç
          </v-btn>
          <v-spacer />
          <v-btn
            :color="responseAction === 'RET' ? 'error' : 'success'"
            depressed
            class="text-capitalize"
            :loading="approvalLoading"
            :disabled="approvalLoading"
            @click="submitResponse"
          >
            Gönder
          </v-btn>
        </template>
        <template v-else>
          <v-spacer />
          <v-btn
            color="success"
            depressed
            class="mr-2 text-capitalize"
            @click="responseAction = 'ONAY'"
          >
            Onayla
          </v-btn>
          <v-btn
            color="error"
            depressed
            class="text-capitalize"
            @click="responseAction = 'RET'"
          >
            Reddet
          </v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref, computed, watch } from "vue-demi";

// Pinia
import { useAppStore } from "@/stores/app";

// Utils
import { useToast } from "vue-toastification/composition";

// Api
import { reconciliationPdf, setMutabakatApproval } from "@/api/reconciliation";

// Icons
import { mdiFileDocumentOutline, mdiCloseCircleOutline, mdiPaperclip } from "@mdi/js";

export default {
  setup() {
    const appStore = useAppStore();
    const toast = useToast();

    // pdf_url her zaman PDF olmayabilir (görsel de olabilir), uzantıya göre karar veriyoruz
    const isImageUrl = (url) => /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i.test(url || "");

    // Kullanıcı kapatılabilir (must_approve olmayan) popup'ı "sonra" dediğinde
    // aynı oturumda tekrar açılmasın diye kapatılan mutabakat no'ları saklanır
    const dismissedMutabakatNos = ref([]);
    const dismiss = () => {
      if (currentMutabakat.value) {
        dismissedMutabakatNos.value.push(currentMutabakat.value.mutabakat_no);
      }
    };

    // must_approve olanlar önce, sonra need_approve olanlar sırayla gösterilir
    const currentMutabakat = computed(() => {
      const list = appStore.pendingMutabakat || [];
      const visible = list.filter(
        (item) => !dismissedMutabakatNos.value.includes(item.mutabakat_no)
      );
      return (
        visible.find((item) => item.must_approve) ||
        visible.find((item) => item.need_approve) ||
        null
      );
    });

    // Mutabakat PDF
    const pdfLoading = ref(false);
    const pdfError = ref(null);
    const pdfUrl = ref(null);
    const loadPdf = async () => {
      pdfLoading.value = true;
      pdfError.value = null;
      pdfUrl.value = null;
      try {
        const response = await reconciliationPdf({
          mutabakat_no: currentMutabakat.value.mutabakat_no,
        });
        if (response.error) {
          throw new Error(response.msg);
        }
        pdfUrl.value = response.response.result;
      } catch (e) {
        pdfError.value = e.message;
      } finally {
        pdfLoading.value = false;
      }
    };

    // Onay/Ret
    const responseAction = ref(null);
    const note = ref("");
    const file = ref(null);
    const approvalLoading = ref(false);

    const cancelResponse = () => {
      responseAction.value = null;
      note.value = "";
      file.value = null;
    };

    const isAllowedFileType = (f) => {
      const type = (f.type || "").toLowerCase();
      if (type.startsWith("image/")) return true;
      if (type === "application/pdf") return true;
      return false;
    };

    const submitResponse = async () => {
      const action = responseAction.value;
      const isReject = action === "RET";
      const trimmedNote = (note.value || "").trim();

      if (isReject && !trimmedNote) {
        toast.error("Ret nedeninizi yazınız", { position: "bottom-center" });
        return;
      }
      if (trimmedNote.length > 255) {
        toast.error("Açıklama en fazla 255 karakter olabilir", { position: "bottom-center" });
        return;
      }
      if (file.value && !isAllowedFileType(file.value)) {
        toast.error("Yalnızca görsel veya PDF dosyası ekleyebilirsiniz", { position: "bottom-center" });
        return;
      }

      approvalLoading.value = true;
      try {
        const response = await setMutabakatApproval({
          mutabakat_no: currentMutabakat.value.mutabakat_no,
          action,
          note: trimmedNote,
          file: file.value,
        });

        if (response.error) {
          throw new Error(response.msg);
        }

        toast.success(
          isReject ? "Mutabakat reddedildi" : "Mutabakat onaylandı",
          { position: "bottom-center" }
        );

        // Onaylanmadığı sürece (RET dahil) sipariş engeli backend tarafında
        // devam eder — need_approve'u ancak firma onayı verildiğinde false
        // döndürür. Bekleyen mutabakat listesi App.vue'da onay geçmişiyle
        // karşılaştırılarak yenilendiği için burada ayrıca cevaplandı
        // işaretlemeye gerek yok.
        if (!isReject) {
          appStore.pendingMutabakat = appStore.pendingMutabakat.filter(
            (item) => item.mutabakat_no !== currentMutabakat.value.mutabakat_no
          );
        } else {
          dismiss();
        }
        cancelResponse();
      } catch (e) {
        toast.error(e.message, { position: "bottom-center" });
      } finally {
        approvalLoading.value = false;
      }
    };

    watch(currentMutabakat, (val) => {
      if (val) {
        cancelResponse();
        loadPdf();
      }
    }, { immediate: true });

    return {
      currentMutabakat,
      dismiss,
      isImageUrl,

      pdfLoading,
      pdfError,
      pdfUrl,

      responseAction,
      note,
      file,
      approvalLoading,
      cancelResponse,
      submitResponse,

      icons: {
        mdiFileDocumentOutline,
        mdiCloseCircleOutline,
        mdiPaperclip,
      },
    };
  },
};
</script>
